import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="flex items-center gap-2 relative">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
            <Activity className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">PULSE / KPI</span>
        </div>
        <div className="relative">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Command Center</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">
            Every metric.<br />One truth.<br />Real time.
          </h1>
          <p className="text-muted-foreground mt-4 max-w-md text-sm">
            Interactive visualizations, drag-and-drop widgets, threshold alerts, and role-based sharing — built for teams that move on data.
          </p>
        </div>
        <div className="relative text-xs text-muted-foreground font-mono">© 2026 Pulse Analytics</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <Activity className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg">PULSE / KPI</span>
          </div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Welcome back</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-8">Sign in to your dashboard</h2>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@kpi.com" required data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full group" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          <div className="mt-6 p-3 rounded-md border border-border bg-card">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Demo accounts</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => quick("admin@kpi.com", "admin123")} className="text-xs font-mono px-2 py-1 rounded border border-border hover:bg-secondary transition-colors" data-testid="demo-admin-button">admin@kpi.com</button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-6">
            No account?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="go-register-link">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
